/**
 * Location Names Module
 * Shared human-readable names for regions and areas
 * Used by both frontend (ui-selectors.js) and backend (spots.mjs)
 */

/**
 * Human-readable region names
 * Keys are lowercase hyphenated IDs from database
 */
export const regionNames = {
    'alabama': 'Alabama',
    'california': 'California',
    'delaware': 'Delaware',
    'florida': 'Florida',
    'hawaii': 'Hawaii',
    'maine': 'Maine',
    'maryland': 'Maryland',
    'massachusetts': 'Massachusetts',
    'new-hampshire': 'New Hampshire',
    'new-jersey': 'New Jersey',
    'new-york': 'New York',
    'north-carolina': 'North Carolina',
    'oregon': 'Oregon',
    'rhode-island': 'Rhode Island',
    'south-carolina': 'South Carolina',
    'texas': 'Texas',
    'virginia': 'Virginia',
    'washington': 'Washington',
};

/**
 * Human-readable area names
 * Keys are lowercase hyphenated IDs from database
 */
export const areaNames = {
    'big-island': 'Big Island',
    'carolina-coast': 'Carolina Coast',
    'central-coast': 'Central Coast',
    'central-florida': 'Central Florida',
    'delaware-coast': 'Delaware Coast',
    'galveston-bay': 'Galveston Bay',
    'gulf-coast': 'Gulf Coast',
    'kauai': 'Kauai',
    'los-angeles-county': 'Los Angeles County',
    'maui': 'Maui',
    'mid-atlantic': 'Mid-Atlantic',
    'new-england': 'New England',
    'new-york': 'New York',
    'north-florida': 'North Florida',
    'oahu': 'Oahu',
    'orange-county': 'Orange County',
    'oregon-coast': 'Oregon Coast',
    'outer-banks': 'Outer Banks',
    'puget-sound': 'Puget Sound',
    'san-diego-county': 'San Diego County',
    'santa-barbara-county': 'Santa Barbara County',
    'south-florida': 'South Florida',
    'south-texas': 'South Texas',
    'ventura-county': 'Ventura County',
    'washington-coast': 'Washington Coast',
};

/**
 * Convert hyphenated-id to Title Case as fallback
 * @param {string} id - Hyphenated ID (e.g., "san-diego-county")
 * @returns {string} Title case string (e.g., "San Diego County")
 */
function toTitleCase(id) {
    if (!id) return '';
    return id
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Get human-readable region name
 * @param {string} regionId - Region ID from database
 * @returns {string} Human-readable region name
 */
export function formatRegionName(regionId) {
    return regionNames[regionId] || toTitleCase(regionId);
}

/**
 * Get human-readable area name
 * @param {string} areaId - Area ID from database
 * @returns {string} Human-readable area name
 */
export function formatAreaName(areaId) {
    return areaNames[areaId] || toTitleCase(areaId);
}

// ============================================================================
// Location Sorting Utilities
// Used by both location panel (ui-selectors.js) and email signup (notifications.js)
// ============================================================================

/**
 * Compare function for region sorting (California first, then alphabetical)
 * Works with both string IDs and objects with id property
 * @param {string|{id: string}} a - First region
 * @param {string|{id: string}} b - Second region
 * @returns {number} Sort comparison result
 */
export function compareRegions(a, b) {
    const aId = typeof a === 'string' ? a : a.id;
    const bId = typeof b === 'string' ? b : b.id;
    if (aId === 'california') return -1;
    if (bId === 'california') return 1;
    return aId.localeCompare(bId);
}

/**
 * Compare function for area sorting (alphabetical by ID)
 * Works with both string IDs and objects with id property
 * @param {string|{id: string}} a - First area
 * @param {string|{id: string}} b - Second area
 * @returns {number} Sort comparison result
 */
export function compareAreas(a, b) {
    const aId = typeof a === 'string' ? a : a.id;
    const bId = typeof b === 'string' ? b : b.id;
    return aId.localeCompare(bId);
}

/**
 * Compare function for spot sorting (alphabetical by name)
 * @param {{name: string}} a - First spot
 * @param {{name: string}} b - Second spot
 * @returns {number} Sort comparison result
 */
export function compareSpots(a, b) {
    return a.name.localeCompare(b.name);
}

/**
 * Sort entire spots data structure (regions array from API)
 * Sorts regions (California first), areas (alphabetical), and spots (alphabetical by name)
 * Mutates and returns the array for convenience
 * @param {Array} regions - Regions array from /api/spots
 * @returns {Array} The sorted regions array
 */
export function sortSpotsData(regions) {
    regions.sort(compareRegions);
    for (const region of regions) {
        if (region.areas) {
            region.areas.sort(compareAreas);
            for (const area of region.areas) {
                if (area.spots) {
                    area.spots.sort(compareSpots);
                }
            }
        }
    }
    return regions;
}
